import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Waves, Flame, Thermometer, Clock, ChevronLeft, Star, MapPin, Plus, Activity, Play } from 'lucide-react';
import LogDipForm from '../components/dips/LogDipForm';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import NewBadgeNotification from '../components/notifications/NewBadgeNotification';
import { useRoleBasedApi } from '../hooks/useRoleBasedApi';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { differenceInCalendarDays, subDays, parseISO, formatDistanceToNow, format } from 'date-fns';
import Confetti from 'react-confetti';
import { toast } from 'sonner';

import UnifiedMobileFooter from '../components/UnifiedMobileFooter';
import { debugLog, debugApi } from '../utils/debug';

// Types
interface Location {
  id: number;
  name: string;
  address?: string;
}
interface Dip {
  id: number;
  location: Location;
  dip_date: string;
  dip_time?: string;
  duration_seconds?: number;
  duration_minutes?: number;
  duration?: number; // backward compatibility for old guest dips
  temperature?: number;
  notes?: string;
  created_at: string;
  type?: 'sauna' | 'cold';
  difficulty?: number; // Added for difficulty rating
  started_at?: string; // Added for new guest dips
}
interface Badge {
  name: string;
  display_name: string;
  description: string;
  icon: string;
  color: string;
  category?: string;
  earned_at?: string;
}

// Helper for formatting
// Remove formatDate, formatTime, dips, setDips if not used
// In the screen where LogDipForm is rendered (e.g., HomeScreen or StartSessionScreen), ensure:
// <LogDipForm locations={locations} onSuccess={handleDipLogged} />
// Remove unused parameters from handleDipLogged if not needed.

// Helper to determine user type
function getUserType(auth: any) {
  if (!auth.user) return 'Visitor';
  if (auth.user.role === 'operator' || (auth.user.roles && auth.user.roles.includes('operator'))) return 'Operator';
  return 'Guest';
}

// Helper to format duration
function formatDuration(dip: Dip): string {
  let seconds = dip.duration_seconds;
  if (seconds == null && dip.duration_minutes != null) seconds = dip.duration_minutes * 60;
  if (seconds == null && dip.duration != null) seconds = dip.duration * 60;
  if (!seconds) return '0 sec';
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  if (min > 0 && sec > 0) return `${min} min ${sec} sec`;
  if (min > 0) return `${min} min`;
  return `${sec} sec`;
}

// Helper to render difficulty as dots
function renderDifficulty(difficulty: number) {
  return (
    <span className="ml-2 text-xs text-sauna-orange align-middle">
      {[1,2,3,4,5].map(i =>
        <span key={i}>{i <= (difficulty || 0) ? '●' : '○'}</span>
      )}
    </span>
  );
}

// Helper to get the best date for a dip
function getDipDate(dip: Dip): string | undefined {
  return dip.dip_date || dip.created_at || dip.started_at;
}

const LogDipPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State machine for screens
  const [screen, setScreen] = useState<'home' | 'start' | 'active' | 'complete' | 'progress'>('home');
  // Data
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [ritualType, setRitualType] = useState<'sauna' | 'cold'>('sauna');
  const [temperature, setTemperature] = useState<number>(75);
  const [difficulty, setDifficulty] = useState<number>(3);
  const [timer, setTimer] = useState<number>(0);
  const [timerActive, setTimerActive] = useState(false);
  const [recentDips, setRecentDips] = useState<Dip[]>([]);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [streaks, setStreaks] = useState<{ sauna: number; cold: number }>({ sauna: 0, cold: 0 });
  const [stats, setStats] = useState<{ week: number; streak: number; total: number; minutes: number }>({ week: 0, streak: 0, total: 0, minutes: 0 });
  const [loading, setLoading] = useState(true);
  const [authChecking, setAuthChecking] = useState(true);
  const { get, post } = useRoleBasedApi();
  const auth = useAuth();
  const { logout } = auth;
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showLinkPrompt, setShowLinkPrompt] = useState(false);
  const [showLinkOnLogin, setShowLinkOnLogin] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkSuccess, setLinkSuccess] = useState(false);
  const [linkingGuestDips, setLinkingGuestDips] = useState(false);
  // Add state to hold the session setup
  const [pendingSession, setPendingSession] = useState<any>(null);
  // Add sessionStep state for two-step flow
  const [sessionStep, setSessionStep] = useState<'location' | 'ritual'>('location');
  // Add state for location search
  const [locationSearch, setLocationSearch] = useState('');
  // Add state for favourites
  const [favouriteLocationIds, setFavouriteLocationIds] = useState<number[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('favourite_locations') || '[]');
    } catch {
      return [];
    }
  });
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);
  
  // Handle URL parameters for pre-selecting location and mode
  useEffect(() => {
    const locationId = searchParams.get('location');
    const mode = searchParams.get('mode');
    
    if (locationId && locations.length > 0) {
      const location = locations.find(loc => loc.id === parseInt(locationId));
      if (location) {
        setSelectedLocation(location);
        
        // If mode is 'live', go directly to start screen
        if (mode === 'live') {
          setScreen('start');
        } else if (mode === 'after') {
          // For 'after' mode, show the LogDipForm directly
          setScreen('home');
        }
      }
    }
  }, [searchParams, locations]);

  // Check authentication and role
  useEffect(() => {
    if (!auth?.user) {
      navigate("/login");
      return;
    }
    // Check if user has guest role (either as primary role or in roles array)
    const hasGuestRole = auth.user.role === 'guest' || 
                        (auth.user.roles && auth.user.roles.includes('guest'));
    if (!hasGuestRole) {
      toast("This page is for guest members only. Please log in as a guest.");
      navigate("/login");
      return;
    }
    setAuthChecking(false);
  }, [auth?.user, navigate]);

  // For visitors (unauthenticated users), we'll use a simpler approach without API calls
  const isVisitor = !auth.user;
  
  // Fetch all dips (all pages)
  const fetchAllDips = async () => {
    let allDips: Dip[] = [];
    let page = 1;
    let hasMore = true;
    try {
      debugLog.dips('Attempting to fetch dips for user:', { userId: auth.user?.id, role: auth.user?.role });
      while (hasMore) {
        const data = await get(`/dips?page=${page}`);
        allDips = allDips.concat(data.dips);
        hasMore = data.has_more;
        page++;
      }
      debugLog.dips('Successfully fetched dips:', { count: allDips.length });
    } catch (error) {
      debugLog.error('Could not fetch dips for user:', error);
      // Return empty array if dips fail to load
      return [];
    }
    return allDips;
  };

  // Fetch user profile for badges
  const fetchBadges = async () => {
    if (!auth.user) return [];
    try {
      const user = await get('/auth/user', { isPublic: true });
      return user.badges || [];
    } catch (error) {
      debugLog.error('Could not fetch badges for guest user:', error);
      return []; // Return empty array for guest users or if badges fail to load
    }
  };

  // Main fetch
  const fetchAll = async () => {
    debugLog.log('fetchAll called with auth.user:', { userId: auth.user?.id, role: auth.user?.role });
    setLoading(true);
    try {
      // For visitors (unauthenticated users), try to fetch locations but skip other API calls
      if (isVisitor) {
        debugLog.locations('Visitor detected, fetching locations only');
        // Try to fetch locations for visitors
        let locations = [];
        try {
          debugLog.locations('Attempting to fetch locations for visitor...');
          const locs = await get('/locations', { isPublic: true });
          locations = locs.data || [];
          debugLog.locations('Successfully fetched locations for visitor:', { count: locations.length });
        } catch (error) {
          debugLog.error('Could not fetch locations for visitor:', error);
          locations = [];
        }
        setLocations(locations);
        const dipsData = JSON.parse(localStorage.getItem('guest_dips') || '[]');
        setRecentDips(dipsData.slice(0, 5));
        setStats({
          week: 0,
          streak: 0,
          total: dipsData.length,
          minutes: 0,
        });
        setStreaks({ sauna: 0, cold: 0 });
        setBadges([]);
        setLoading(false);
        return;
      }
      // Try to fetch locations, but don't fail if it doesn't work
      let locations = [];
      try {
        debugLog.locations('Attempting to fetch locations...');
        const locs = await get('/locations', { isPublic: true });
        locations = locs.data || [];
        debugLog.locations('Successfully fetched locations:', { count: locations.length });
      } catch (error) {
        debugLog.error('Could not fetch locations:', error);
        locations = [];
      }
      setLocations(locations);
      let dipsData = [];
      if (auth.user) {
        debugLog.dips('User is logged in, fetching dips...');
        try {
          dipsData = await fetchAllDips();
        } catch (error) {
          debugLog.error('Could not fetch dips for authenticated user, using localStorage:', error);
          dipsData = JSON.parse(localStorage.getItem('guest_dips') || '[]');
        }
      } else {
        debugLog.dips('No user, using guest dips from localStorage');
        dipsData = JSON.parse(localStorage.getItem('guest_dips') || '[]');
      }
      // Sort dipsData by date/time descending (most recent first)
      dipsData.sort((a: Dip, b: Dip) => {
        const dateA = getDipDate(a);
        const dateB = getDipDate(b);
        if (!dateA || !dateB) return 0;
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      });
      setRecentDips(dipsData.slice(0, 5));
      // Stats
      const now = new Date();
      const weekAgo = subDays(now, 6);
      const dipsThisWeek = dipsData.filter((dip: Dip) => {
        const dateStr = getDipDate(dip);
        if (!dateStr) return false;
        return parseISO(dateStr) >= weekAgo;
      });
      const totalMinutesThisWeek = dipsThisWeek.reduce((sum: number, dip: Dip) => sum + (dip.duration_minutes || dip.duration || (dip.duration_seconds ? Math.round(dip.duration_seconds / 60) : 0)), 0);
      setStats({
        week: dipsThisWeek.length,
        streak: calculateDayStreak(dipsData),
        total: dipsData.length,
        minutes: totalMinutesThisWeek,
      });
      setStreaks({
        sauna: calculateTypeStreak(dipsData, 'sauna'),
        cold: calculateTypeStreak(dipsData, 'cold'),
      });
      // Badges
      if (auth.user) {
        try {
          const badgesData = await fetchBadges();
          setBadges(badgesData);
        } catch (error) {
          setBadges([]);
        }
      } else {
        setBadges([]);
      }
      setLoading(false);
    } catch (error) {
      debugLog.error('Error in fetchAll:', error);
      setLoading(false);
    }
  };
  
  // Fetch all data on mount
  useEffect(() => {
    console.log('useEffect triggered, auth.user:', auth.user?.id, auth.user?.role);
    fetchAll();
    // eslint-disable-next-line
  }, [auth.user]);

  // Timer logic
  useEffect(() => {
    if (timerActive) {
      timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [timerActive]);

  // On login, check for guest dips
  useEffect(() => {
    if (auth.user && localStorage.getItem('guest_dips')) {
      setShowLinkOnLogin(true);
    }
  }, [auth.user]);
  
  // Show loading while checking authentication
  if (authChecking) {
    return (
      <div className="flex items-center justify-center">
        <LoadingSpinner size="xl" variant="default" />
      </div>
    );
  }

  // Toggle favourite
  function toggleFavouriteLocation(id: number) {
    setFavouriteLocationIds(prev => {
      let updated;
      if (prev.includes(id)) {
        updated = prev.filter(fid => fid !== id);
      } else {
        updated = [...prev, id];
      }
      localStorage.setItem('favourite_locations', JSON.stringify(updated));
      return updated;
    });
    // Refocus the search input after toggling
    if (searchInputRef.current) searchInputRef.current.focus();
  }

  // Calculate day streak (consecutive days with at least one dip)
  function calculateDayStreak(dips: Dip[]): number {
    if (!dips.length) return 0;
    const days = Array.from(new Set(dips.map(d => getDipDate(d)).filter(Boolean)));
    days.sort((a, b) => parseISO(b).getTime() - parseISO(a).getTime());
    let streak = 1;
    for (let i = 1; i < days.length; i++) {
      const diff = differenceInCalendarDays(parseISO(days[i - 1]), parseISO(days[i]));
      if (diff === 1) streak++;
      else break;
    }
    return streak;
  }

  // Calculate type streak (consecutive dips of a type)
  function calculateTypeStreak(dips: Dip[], type: 'sauna' | 'cold'): number {
    if (!dips.length) return 0;
    const filtered = dips.filter(d => d.type === type);
    if (!filtered.length) return 0;
    const days = Array.from(new Set(filtered.map(d => getDipDate(d)).filter(Boolean)));
    days.sort((a, b) => parseISO(b).getTime() - parseISO(a).getTime());
    let streak = 1;
    for (let i = 1; i < days.length; i++) {
      const diff = differenceInCalendarDays(parseISO(days[i - 1]), parseISO(days[i]));
      if (diff === 1) streak++;
      else break;
    }
    return streak;
  }

  const handleSendMagicLink = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would trigger your backend magic link flow
    setLinkSuccess(true);
    setTimeout(() => setShowLinkPrompt(false), 2000);
  };

  const handleLinkGuestDips = async () => {
    setLinkingGuestDips(true);
    const guestDips = JSON.parse(localStorage.getItem('guest_dips') || '[]');
    let successCount = 0;
    let failCount = 0;
    if (auth.user && guestDips.length > 0) {
      try {
        // Post each dip to the backend
        for (const dip of guestDips) {
          // Remove any fields not needed by backend (like id)
          const { id, ...dipData } = dip;
          try {
            await post('/dips', dipData);
            successCount++;
          } catch (e) {
            failCount++;
            console.error('Failed to link dip:', dipData, e);
            if (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string' && e.message.toLowerCase().includes('user info')) {
              toast.error(
                <>
                  We could not load your account details. Please try logging in again or contact support if the problem persists.
                  <button
                    className="ml-2 underline text-sauna-orange"
                    onClick={() => logout && logout()}
                  >
                    Log out
                  </button>
                </>,
                {
                  action: {
                    label: 'Retry',
                    onClick: () => fetchAll(),
                  },
                  duration: 10000,
                  dismissible: true,
                }
              );
              break;
            }
          }
        }
        localStorage.removeItem('guest_dips');
        if (successCount > 0 && failCount === 0) {
          toast.success(`${successCount} session${successCount > 1 ? 's' : ''} linked to your profile!`);
        } else if (successCount > 0 && failCount > 0) {
          toast.warning(`${successCount} session${successCount > 1 ? 's' : ''} linked, ${failCount} failed.`);
        } else if (failCount > 0) {
          toast.error('Failed to link any sessions.');
        }
        fetchAll();
      } catch (e) {
        if (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string' && e.message.toLowerCase().includes('user info')) {
          toast.error(
            <>
              We could not load your account details. Please try logging in again or contact support if the problem persists.
              <button
                className="ml-2 underline text-sauna-orange"
                onClick={() => logout && logout()}
              >
                Log out
              </button>
            </>,
            {
              action: {
                label: 'Retry',
                onClick: () => fetchAll(),
              },
              duration: 10000,
              dismissible: true,
            }
          );
        } else {
          toast.error('Failed to link some visitor dips.');
        }
      }
    } else {
      localStorage.removeItem('guest_dips');
      toast.success('Visitor dips linked to your profile!');
    }
    setLinkingGuestDips(false);
    setShowLinkOnLogin(false);
  };

  // Update handleStartSession to only set up the session and move to 'active'
  const handleStartSession = () => {
    const session = {
      location: selectedLocation,
      type: ritualType,
      temperature,
      started_at: new Date().toISOString(),
    };
    setPendingSession(session);
    setScreen('active');
    setTimer(0);
    setTimerActive(true);
  };

  // Add a handler to log the session when 'Complete Session' is clicked
  const handleCompleteSession = async () => {
    if (!pendingSession) return;
    setTimerActive(false);
    const now = new Date().toISOString();
    const session = {
      ...pendingSession,
      duration_seconds: timer, // always use duration_seconds
      difficulty,
      started_at: now,
      dip_date: now,
      created_at: now,
      completed_at: now,
    };
    console.log('handleCompleteSession: auth.user =', auth.user);
    if (auth.user) {
      try {
        await post('/dips', session);
        toast.success('Session logged!');
        setScreen('home');
        fetchAll();
      } catch (e) {
        toast.error('Failed to log session.');
      }
    } else {
      console.log('Logging session as visitor (localStorage only)', session);
      const guestDips = JSON.parse(localStorage.getItem('guest_dips') || '[]');
      guestDips.push(session);
      localStorage.setItem('guest_dips', JSON.stringify(guestDips));
      toast.success('Session logged as visitor!');
      setScreen('home');
      fetchAll();
    }
    setPendingSession(null);
  };

  // --- Screens ---
  // 1. Home/Welcome
  // HomeScreen: interactive buttons
  const HomeScreen = () => {
    const userType = getUserType(auth);
    function getRelativeDate(dateStr: string) {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '-';
      const now = new Date();
      const diffDays = differenceInCalendarDays(now, date);
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      return format(date, 'MMM d');
    }
    return (
      <div className="bg-gray-50 px-2 py-2 pt-0">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8 flex flex-col items-center">
            <h2 className="text-xl font-bold text-center mb-1">Home Screen</h2>
            <div className="text-2xl font-extrabold text-gray-900 text-center mb-1 mt-2">Welcome Back</div>
            <div className="text-gray-500 text-base text-center mb-6">Ready for your next ritual?</div>
            <div className="flex w-full justify-between gap-3 mb-7">
              <div className="flex-1 flex flex-col items-center bg-[#FFE6DB] rounded-xl py-3">
                <span className="text-2xl font-bold text-sauna-orange">{stats.week}</span>
                <span className="text-xs text-gray-700 mt-1">This Week</span>
              </div>
              <div className="flex-1 flex flex-col items-center bg-[#E6F0FA] rounded-xl py-3">
                <span className="text-2xl font-bold text-sauna-blue">{stats.streak}</span>
                <span className="text-xs text-gray-700 mt-1">Day Streak</span>
              </div>
              <div className="flex-1 flex flex-col items-center bg-[#E6FAE6] rounded-xl py-3">
                <span className="text-2xl font-bold text-green-600">{stats.total}</span>
                <span className="text-xs text-gray-700 mt-1">Total Sessions</span>
              </div>
            </div>
            {/* Start New Session Button */}
            <Button className="w-full bg-sauna-orange hover:bg-sauna-orange/90 text-white text-lg rounded-full py-3 mb-3 shadow-md transition-all duration-150" onClick={() => { setScreen('start'); setSessionStep('location'); setSelectedLocation(null); }}>Start New Session</Button>
            {/* View Progress Button */}
            <Button variant="outline" className="w-full rounded-full py-3 mb-6 border-2 border-gray-200 text-gray-900 font-semibold bg-white shadow-sm hover:bg-gray-50 transition-all duration-150" onClick={() => setScreen('progress')}>View Progress</Button>
            {/* Recent Sessions Card */}
            <div className="w-full bg-[#F8F8F8] rounded-2xl shadow p-5 mt-2">
              <div className="text-sm font-semibold text-gray-700 mb-3">Recent Sessions</div>
              {recentDips.length === 0 && (
                <div className="text-xs text-gray-400">No recent sessions</div>
              )}
              {recentDips.map((dip, idx) => (
                <div key={dip.id ? dip.id : `${dip.created_at}-${dip.type}-${idx}`} className="flex items-center gap-2 text-base mb-2 last:mb-0">
                  {dip.type === 'sauna' ? <Flame className="w-4 h-4 text-sauna-orange" /> : <Waves className="w-4 h-4 text-sauna-blue" />}
                  <span className="font-medium text-gray-900">{dip.type === 'sauna' ? 'Sauna' : 'Cold Plunge'}</span>
                  <span className="text-gray-400">-</span>
                  <span className="text-gray-700">{formatDuration(dip)}</span>
                  {renderDifficulty(dip.difficulty)}
                  <span className="ml-auto text-xs text-gray-500">{getRelativeDate(dip.dip_date || dip.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 2. Start Session: Two-step flow
  // Add state: const [sessionStep, setSessionStep] = useState<'location' | 'ritual'>('location');
  // Add state: const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  //
  // In the main render, conditionally render:
  // if (screen === 'start' && sessionStep === 'location') render LocationPickerScreen
  // if (screen === 'start' && sessionStep === 'ritual') render RitualTypeScreen
  //
  // LocationPickerScreen: Card layout, list locations with name, subtitle (types), and mock distance (e.g., 0.5km, 1.2km, ...)
  // On select, setSelectedLocation(loc), setSessionStep('ritual')
  //
  // RitualTypeScreen: Card for selected location, toggle for Sauna/Cold Dip, large temp, recommended range, slider, Start Ritual button (color by type)
  //
  // 2. Home: Stat cards (This Week, Day Streak, Total Sessions), large Start New Session button, View Progress button, Recent Sessions (icon, label, duration, relative date)
  //
  // 3. Progress: Streak cards, badge grid, week stats card
  //
  // 4. Session Active/Complete: Session type card, timer, encouragement, temp card, styled buttons
  //
  // 5. All screens mobile-friendly, use SaunaDip color palette, and render <MobileFooterNav /> at the bottom
  //
  // (Implement all UI as per screenshots, using Tailwind and lucide-react icons)
  // StartSessionScreen: interactive navigation
  const StartSessionScreen = () => {
    if (sessionStep === 'location') {
      // Filter locations by search and favourites
      let filteredLocations = locations.filter(loc =>
        loc.name.toLowerCase().includes(locationSearch.toLowerCase())
      );
      if (showFavouritesOnly) {
        filteredLocations = filteredLocations.filter(loc => favouriteLocationIds.includes(loc.id));
      }
      return (
        <div className="bg-[#FAFAF8] px-2 py-6">
          <div className="w-full max-w-md mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-8 flex flex-col items-center">
              <h2 className="text-xl font-bold text-center mb-1">Start Session</h2>
              <div className="text-lg font-semibold text-gray-900 text-center mb-2 mt-2">Choose Location</div>
              <div className="text-gray-500 text-base text-center mb-6">Pick where your ritual will take place</div>
              {/* Favourites toggle */}
              <button
                className={`flex items-center gap-2 mb-3 px-3 py-1 rounded-full border transition text-sm font-medium
                  ${showFavouritesOnly ? 'bg-sauna-orange/10 border-sauna-orange/30 text-sauna-orange' : 'bg-gray-50 border-gray-200 text-gray-500'}`}
                onClick={() => setShowFavouritesOnly(v => !v)}
              >
                <Star className={`w-4 h-4 ${showFavouritesOnly ? 'fill-sauna-orange text-sauna-orange' : 'text-sauna-orange/40'}`} fill={showFavouritesOnly ? '#F5542D' : 'none'} />
                {showFavouritesOnly ? 'Showing Favourites' : 'Show Favourites Only'}
              </button>
              {/* Search input */}
              <input
                ref={searchInputRef}
                type="text"
                value={locationSearch}
                onChange={e => setLocationSearch(e.target.value)}
                placeholder="Search locations..."
                className="w-full mb-4 px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-sauna-orange text-base"
              />
              {/* Scrollable list */}
              <div className="w-full flex flex-col gap-4 max-h-96 overflow-y-auto">
                {filteredLocations.length === 0 && (
                  <div className="text-center text-gray-400">No locations found</div>
                )}
                {filteredLocations.map((loc) => (
                  <div key={loc.id} className="relative">
                    <button
                      className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all duration-150 shadow-sm text-left
                        ${selectedLocation?.id === loc.id ? 'border-sauna-orange bg-[#FFE6DB]' : 'border-gray-200 bg-gray-50 hover:border-sauna-orange/60'}`}
                      onClick={() => { setSelectedLocation(loc); setSessionStep('ritual'); }}
                    >
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-sauna-orange/10 text-sauna-orange mr-2">
                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"></path><circle cx="12" cy="9" r="2.5"></circle></svg>
                      </span>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{loc.name}</div>
                        <div className="text-xs text-gray-500">Selected location</div>
                      </div>
                    </button>
                    {/* Favourite star button */}
                    <button
                      className="absolute top-3 right-3 p-1 rounded-full hover:bg-sauna-orange/10"
                      onClick={e => { e.stopPropagation(); toggleFavouriteLocation(loc.id); }}
                      title={favouriteLocationIds.includes(loc.id) ? 'Remove from favourites' : 'Add to favourites'}
                    >
                      <Star className={`w-5 h-5 ${favouriteLocationIds.includes(loc.id) ? 'fill-sauna-orange text-sauna-orange' : 'text-sauna-orange/40'}`} fill={favouriteLocationIds.includes(loc.id) ? '#F5542D' : 'none'} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }
    // Ritual step: Start Ritual button is interactive
    return (
      <div className="bg-[#FAFAF8] px-2 py-6">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8 flex flex-col items-center">
            <div className="flex items-center w-full mb-2">
              <button className="mr-2" onClick={() => setSessionStep('location')}>
                <ChevronLeft className="w-6 h-6 text-gray-400" />
              </button>
              <h2 className="text-xl font-bold text-center flex-1">Start Session</h2>
            </div>
            <div className="text-gray-500 text-base text-center mb-6 w-full">Choose your location and ritual</div>
            <div className="w-full bg-[#FFE6DB] border-2 border-sauna-orange rounded-xl flex items-center gap-3 p-4 mb-6">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-sauna-orange/10 text-sauna-orange mr-2">
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"></path><circle cx="12" cy="9" r="2.5"></circle></svg>
              </span>
              <div className="flex-1">
                <div className="font-semibold text-gray-900">{selectedLocation?.name}</div>
                <div className="text-xs text-gray-500">Selected location</div>
              </div>
            </div>
            <div className="flex w-full justify-center gap-3 mb-6">
              <button
                className={`flex-1 flex items-center justify-center gap-2 rounded-full py-2 text-lg font-semibold transition-all duration-150 ${ritualType === 'sauna' ? 'bg-sauna-orange text-white shadow' : 'bg-gray-100 text-gray-700'}`}
                onClick={() => setRitualType('sauna')}
              >
                <Flame className="w-5 h-5" /> Sauna
              </button>
              <button
                className={`flex-1 flex items-center justify-center gap-2 rounded-full py-2 text-lg font-semibold transition-all duration-150 ${ritualType === 'cold' ? 'bg-sauna-blue text-white shadow' : 'bg-gray-100 text-gray-700'}`}
                onClick={() => setRitualType('cold')}
              >
                <Waves className="w-5 h-5" /> Cold Dip
              </button>
            </div>
            <div className="text-center mb-2 w-full">
              <div className="text-4xl font-extrabold text-gray-900">{temperature}&deg;C</div>
              <div className="text-xs text-gray-500">Recommended: {ritualType === 'sauna' ? '75-90°C' : '0-15°C'}</div>
            </div>
            <div className="flex items-center gap-2 mb-8 w-full">
              <span className="text-xs text-gray-400">{ritualType === 'sauna' ? '50°C' : '0°C'}</span>
              <input
                type="range"
                min={ritualType === 'sauna' ? 50 : 0}
                max={ritualType === 'sauna' ? 120 : 20}
                value={temperature}
                onChange={e => setTemperature(Number(e.target.value))}
                className="flex-1 accent-sauna-orange h-2 rounded-full"
              />
              <span className="text-xs text-gray-400">{ritualType === 'sauna' ? '120°C' : '20°C'}</span>
            </div>
            <Button className="w-full bg-sauna-orange hover:bg-sauna-orange/90 text-white text-lg rounded-full py-3 mt-2 shadow-md transition-all duration-150" onClick={() => {
              const session = {
                location: selectedLocation,
                type: ritualType,
                temperature,
                started_at: new Date().toISOString(),
              };
              setPendingSession(session);
              setScreen('active');
              setTimer(0);
              setTimerActive(true);
            }}>
              Start Ritual
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // 3. Session Active
  // SessionActiveScreen: interactive navigation
  const SessionActiveScreen = () => (
    <div className="bg-[#FAFAF8] px-2 py-6">
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl p-8 flex flex-col items-center">
          <h2 className="text-xl font-bold text-center mb-1">Session Active</h2>
          <div className="text-lg font-semibold text-gray-900 text-center mb-1 mt-2">Your Ritual</div>
          <div className="text-gray-500 text-base text-center mb-6">Track your session progress</div>
          {/* Session type card */}
          <div className="w-full bg-[#FFE6DB] border-2 border-sauna-orange rounded-xl flex items-center gap-3 p-4 mb-6">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-sauna-orange/10 text-sauna-orange mr-2">
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"></path><circle cx="12" cy="9" r="2.5"></circle></svg>
            </span>
            <div className="flex-1">
              <div className="font-semibold text-gray-900">{selectedLocation?.name}</div>
              <div className="text-xs text-gray-500">Active session</div>
            </div>
          </div>
          {/* Timer */}
          <div className="text-4xl font-bold text-sauna-orange mb-6">
            {Math.floor(timer / 60).toString().padStart(2, '0')}:{(timer % 60).toString().padStart(2, '0')}
          </div>
          <div className="text-gray-500 text-sm mb-6 flex items-center gap-2 justify-center">
            <div className={`w-2 h-2 rounded-full ${timerActive ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'}`}></div>
            {timerActive ? 'Session duration' : 'Timer paused'}
          </div>
          {/* Temperature card */}
          <div className="w-full bg-[#E6F0FA] border-2 border-sauna-blue rounded-xl p-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-sauna-blue">85°C</div>
              <div className="text-sm text-gray-600">Current temperature</div>
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex w-full gap-3">
            <Button 
              variant="outline" 
              className="flex-1 rounded-full py-3 border-2 border-gray-200 text-gray-900 font-semibold bg-white shadow-sm hover:bg-gray-50 transition-all duration-150"
              onClick={() => setTimerActive(!timerActive)}
            >
              {timerActive ? 'Pause' : 'Resume'}
            </Button>
            <Button 
              className="flex-1 bg-red-500 hover:bg-red-600 text-white text-lg rounded-full py-3 shadow-md transition-all duration-150"
              onClick={handleCompleteSession}
            >
              End Session
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // 4. Session Complete
  // SessionCompleteScreen: interactive navigation
  const SessionCompleteScreen = () => (
    <div className="bg-[#FAFAF8] px-2 py-6">
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl p-8 flex flex-col items-center">
          <h2 className="text-xl font-bold text-center mb-1">Session Complete</h2>
          <div className="text-lg font-semibold text-gray-900 text-center mb-1 mt-2">Great Job!</div>
          <div className="text-gray-500 text-base text-center mb-6">Your session has been logged</div>
          {/* Session summary */}
          <div className="w-full bg-[#FFE6DB] border-2 border-sauna-orange rounded-xl p-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-sauna-orange">
                {Math.floor(timer / 60).toString().padStart(2, '0')}:{(timer % 60).toString().padStart(2, '0')}
              </div>
              <div className="text-sm text-gray-600">Session duration</div>
            </div>
          </div>
          {/* Temperature card */}
          <div className="w-full bg-[#E6F0FA] border-2 border-sauna-blue rounded-xl p-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-sauna-blue">85°C</div>
              <div className="text-sm text-gray-600">Average temperature</div>
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex w-full gap-3">
            <Button variant="outline" className="flex-1 rounded-full py-3 border-2 border-gray-200 text-gray-900 font-semibold bg-white shadow-sm hover:bg-gray-50 transition-all duration-150">View Progress</Button>
            <Button className="flex-1 bg-sauna-orange hover:bg-sauna-orange/90 text-white text-lg rounded-full py-3 shadow-md transition-all duration-150">Start New Session</Button>
          </div>
        </div>
      </div>
    </div>
  );

  // 5. Progress Overview
  const ProgressOverviewScreen = () => (
    <div className="bg-[#FAFAF8] px-2 py-6">
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl p-8 flex flex-col items-center relative">
          {/* Back button */}
          <button
            className="absolute top-4 left-4 p-2 rounded-full bg-white border border-sauna-blue/20 text-sauna-blue hover:bg-sauna-blue/10 transition"
            onClick={() => setScreen('home')}
            aria-label="Back to Home"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h2 className="text-xl font-bold text-center mb-1">Progress Overview</h2>
          <div className="text-lg font-semibold text-gray-900 text-center mb-1 mt-2">Your Journey</div>
          <div className="text-gray-500 text-base text-center mb-6">Track your ritual progress</div>
          {/* Streak Cards */}
          <div className="flex w-full justify-between gap-4 mb-6">
            <div className="flex-1 flex flex-col items-center border-2 border-sauna-blue bg-[#E6F0FA] rounded-xl py-4">
              <Waves className="w-6 h-6 text-sauna-blue mb-1" />
              <span className="text-2xl font-bold text-sauna-blue">{streaks.cold}</span>
              <span className="text-xs text-gray-700 mt-1">Cold Plunges</span>
            </div>
            <div className="flex-1 flex flex-col items-center border-2 border-sauna-orange bg-[#FFE6DB] rounded-xl py-4">
              <Flame className="w-6 h-6 text-sauna-orange mb-1" />
              <span className="text-2xl font-bold text-sauna-orange">{streaks.sauna}</span>
              <span className="text-xs text-gray-700 mt-1">Sauna Sessions</span>
            </div>
          </div>
          {/* Badge Grid */}
          <div className="w-full mb-6">
            <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><span role='img' aria-label='medal'>🏅</span> Earned Badges ({badges.length})</div>
            <div className="grid grid-cols-2 gap-3">
              {badges.length === 0 && <div className="text-xs text-gray-400 col-span-2">No badges yet</div>}
              {badges.map(badge => (
                <div key={badge.name} className="flex flex-col items-center bg-[#F8F8F8] rounded-xl p-3 shadow text-center">
                  <span className="text-2xl mb-1">{badge.icon}</span>
                  <span className="font-semibold text-xs text-gray-900">{badge.display_name}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Week Stats Card */}
          <div className="w-full bg-gradient-to-r from-[#FFE6DB] via-[#E6F0FA] to-[#E6FAE6] rounded-2xl shadow p-6 flex justify-between items-center">
            <div className="flex flex-col items-center flex-1">
              <span className="text-2xl font-bold text-sauna-orange">{stats.week}</span>
              <span className="text-xs text-gray-700 mt-1">Total Sessions</span>
            </div>
            <div className="flex flex-col items-center flex-1">
              <span className="text-2xl font-bold text-sauna-blue">{stats.minutes}</span>
              <span className="text-xs text-gray-700 mt-1">Minutes</span>
            </div>
            <div className="flex flex-col items-center flex-1">
              <span className="text-2xl font-bold text-green-600">{stats.streak}</span>
              <span className="text-xs text-gray-700 mt-1">Day Streak</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // StatCard helper
  function StatCard({ value, label, color }: { value: number; label: string; color: string }) {
    return (
      <div className="flex flex-col items-center flex-1">
        <div className={`font-bold text-xl ${color}`}>{value}</div>
        <div className="text-xs text-gray-500 text-center whitespace-nowrap">{label}</div>
      </div>
    );
  }

  return (
    <>
      {showConfetti && <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={250} />}
      {/* Magic link prompt after guest logs a dip */}
      {showLinkPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full">
            {linkSuccess ? (
              <div className="text-center text-green-600 font-semibold">Magic link sent! Check your email.</div>
            ) : (
              <form onSubmit={handleSendMagicLink} className="flex flex-col gap-3">
                <div className="font-semibold text-lg mb-2">Link your dip to your profile</div>
                <p className="text-gray-600 text-sm mb-2">Enter your email to get a magic link and connect this dip to your SaunaDip account.</p>
                <input type="email" required value={linkEmail} onChange={e => setLinkEmail(e.target.value)} placeholder="you@email.com" className="rounded border px-3 py-2 w-full" />
                <Button type="submit" className="w-full bg-sauna-orange text-white mt-2">Send Magic Link</Button>
                <Button type="button" variant="ghost" className="w-full mt-1" onClick={() => setShowLinkPrompt(false)}>Maybe later</Button>
              </form>
            )}
          </div>
        </div>
      )}
      {/* Link guest dips on login prompt */}
      {showLinkOnLogin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full text-center">
            <div className="font-semibold text-lg mb-2">Link your dips</div>
            <p className="text-gray-600 text-sm mb-4">You have dips logged. Would you like to link them to your profile?</p>
            <Button onClick={handleLinkGuestDips} className="w-full bg-sauna-orange text-white mb-2" disabled={linkingGuestDips}>{linkingGuestDips ? 'Linking...' : 'Link Dips'}</Button>
            <Button variant="ghost" className="w-full" onClick={() => setShowLinkOnLogin(false)}>No, thanks</Button>
          </div>
        </div>
      )}
      {/* Significantly reduce spacing to move form much higher up: */}
      <div className="container mx-auto px-4 py-2">
            {/* Common container for message and form */}
    <div className="max-w-md mx-auto">
      {/* Integrated visitor message as part of form text */}
      {/* Form content - now directly below integrated message */}
      <div>
        {screen === 'home' && <HomeScreen />}
        {screen === 'start' && <StartSessionScreen />}
        {screen === 'active' && <SessionActiveScreen />}
        {screen === 'complete' && <SessionCompleteScreen />}
        {screen === 'progress' && <ProgressOverviewScreen />}
      </div>
    </div>
      </div>
              <UnifiedMobileFooter />
    </>
  );
};

export default LogDipPage; 